import { useState } from 'react';
import {
  Text,
  Card,
  Button,
  Input,
  Modal,
  Table,
  EmptyState,
  Badge,
} from '@teals/ui-web';
import type { Column } from '@teals/ui-web';
import {
  useHardDeleteUserMutation,
  useRestoreUserMutation,
  useSearchStaffUsersQuery,
  useSoftDeleteUserMutation,
  type StaffUserItem,
} from '../../store/api/staffApi';
import { typedConfirmMatches } from './typedConfirm';

export function UsersPanel() {
  const [q, setQ] = useState('');
  const [softTarget, setSoftTarget] = useState<StaffUserItem | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<StaffUserItem | null>(null);
  const [hardTarget, setHardTarget] = useState<StaffUserItem | null>(null);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const { data, isFetching, isError, refetch } = useSearchStaffUsersQuery({ q });
  const [softDelete, { isLoading: softDeleting }] = useSoftDeleteUserMutation();
  const [restoreUser, { isLoading: restoring }] = useRestoreUserMutation();
  const [hardDelete, { isLoading: hardDeleting }] = useHardDeleteUserMutation();

  const items = data?.items || [];
  const emailMatches = typedConfirmMatches(confirmEmail, hardTarget?.email || '');

  const messageFrom = (err: unknown, fallback: string) => {
    const data = (err as { data?: { message?: string } })?.data;
    return data?.message || fallback;
  };

  const columns: Column<StaffUserItem>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (value) => (value as string) || '—',
    },
    { key: 'email', header: 'Email' },
    {
      key: 'organisations',
      header: 'Organisations',
      render: (value) => ((value as string[]) || []).join(', ') || '—',
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => (
        <Badge variant={value === 'active' ? 'success' : 'warning'}>
          {value === 'active' ? 'Active' : 'Disabled'}
        </Badge>
      ),
    },
    {
      key: 'userId',
      header: '',
      render: (_value, row) => (
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
          {row.status === 'active' ? (
            <Button size="sm" variant="secondary" onClick={() => setSoftTarget(row)}>
              Soft delete
            </Button>
          ) : (
            <Button size="sm" variant="secondary" onClick={() => setRestoreTarget(row)}>
              Restore
            </Button>
          )}
          <Button
            size="sm"
            variant="danger"
            onClick={() => {
              setConfirmEmail('');
              setHardTarget(row);
            }}
          >
            Hard delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ marginTop: '1rem' }}>
      <Text variant="body2" style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
        Soft delete disables sign-in and can be restored. Hard delete removes the
        Cognito user and cannot be undone.
      </Text>
      {errorMsg ? (
        <Card style={{ padding: '1rem', marginBottom: '1rem' }}>
          <Text variant="body2">{errorMsg}</Text>
        </Card>
      ) : null}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
        <Input
          style={{ flex: 1 }}
          placeholder="Search name, email, or id…"
          value={q}
          onChange={(event) => setQ(event.target.value)}
          aria-label="Search users"
        />
      </div>
      {isError ? (
        <EmptyState
          title="Could not load users"
          description="Check that you are in the staff Cognito group."
        />
      ) : !isFetching && items.length === 0 ? (
        <EmptyState
          title="No users found"
          description="Try a different name, email, or id."
        />
      ) : (
        <Table columns={columns} data={items} rowKey="userId" loading={isFetching} />
      )}

      <Modal
        isOpen={!!softTarget}
        onClose={() => setSoftTarget(null)}
        title="Soft delete user"
      >
        <Text variant="body2">
          {softTarget?.name || softTarget?.email} will not be able to sign in.
          Their profile and memberships stay.
        </Text>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <Button
            variant="danger"
            disabled={softDeleting}
            onClick={() => {
              if (!softTarget) return;
              setErrorMsg(null);
              void softDelete({ userId: softTarget.userId, confirm: true })
                .unwrap()
                .then(() => {
                  setSoftTarget(null);
                  void refetch();
                })
                .catch((err) => setErrorMsg(messageFrom(err, 'Soft delete failed.')));
            }}
          >
            Disable sign-in
          </Button>
          <Button variant="ghost" onClick={() => setSoftTarget(null)}>
            Cancel
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={!!restoreTarget}
        onClose={() => setRestoreTarget(null)}
        title="Restore user"
      >
        <Text variant="body2">
          Turn sign-in back on for {restoreTarget?.name || restoreTarget?.email}?
        </Text>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <Button
            disabled={restoring}
            onClick={() => {
              if (!restoreTarget) return;
              setErrorMsg(null);
              void restoreUser({ userId: restoreTarget.userId, confirm: true })
                .unwrap()
                .then(() => {
                  setRestoreTarget(null);
                  void refetch();
                })
                .catch((err) => setErrorMsg(messageFrom(err, 'Restore failed.')));
            }}
          >
            Restore
          </Button>
          <Button variant="ghost" onClick={() => setRestoreTarget(null)}>
            Cancel
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={!!hardTarget}
        onClose={() => setHardTarget(null)}
        title="Hard delete user"
      >
        <Text variant="body2">
          This removes {hardTarget?.name || 'this user'} and their Cognito user.
          It cannot be undone. Type {hardTarget?.email} to enable delete.
        </Text>
        <Input
          style={{ marginTop: '1rem' }}
          placeholder="Email"
          value={confirmEmail}
          onChange={(event) => setConfirmEmail(event.target.value)}
          aria-label="Confirm email"
        />
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <Button
            variant="danger"
            disabled={!emailMatches || hardDeleting}
            onClick={() => {
              if (!hardTarget) return;
              setErrorMsg(null);
              void hardDelete({
                userId: hardTarget.userId,
                confirmEmail: confirmEmail.trim(),
              })
                .unwrap()
                .then(() => {
                  setHardTarget(null);
                  setConfirmEmail('');
                  void refetch();
                })
                .catch((err) => setErrorMsg(messageFrom(err, 'Hard delete failed.')));
            }}
          >
            Delete permanently
          </Button>
          <Button variant="ghost" onClick={() => setHardTarget(null)}>
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
}
